'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowRight } from '@/components/ui/icons'
import { useExtracted } from 'next-intl'
import { trackPricingPageViewed } from '@/lib/analytics'
import { useAuth } from '@/hooks/useAuth'
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess'
import { SignUpModal } from '@/components/auth/SignUpModal'

export function PricingCTA() {
  const t = useExtracted()
  const router = useRouter()
  const hasTrackedPageView = useRef(false)
  const { auth, isLoading: authLoading, isAnonymous } = useAuth()
  const hasSignupAccess = useStoredSignupAccess()
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  // Match PlanCards: signed-in users keep today's CTA even if a stored
  // code lingers in localStorage (e.g. a promo link visited while logged in).
  const isRealAccount = !!auth?.userId && !isAnonymous

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPricingPageViewed()
      hasTrackedPageView.current = true
    }
  }, [])

  const ctaClassName =
    'group/btn inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-8 py-4 text-base font-semibold text-black transition-all duration-200 hover:bg-white/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 md:w-auto'

  return (
    <div className="mx-auto mb-16 max-w-2xl">
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center md:p-10">
        <div className="relative z-10">
          <h3 className="mb-3 text-xl font-semibold text-white md:mb-4 md:text-2xl">{t('Ready to get started?')}</h3>
          <p className="mb-6 text-base leading-relaxed text-white/50">
            {t('For creatives, studios, and agencies who create professional AI videos.')}
          </p>
          {/* While auth initializes, a stored code makes the right CTA
              ambiguous — hold like the plan cards do instead of letting a
              quick click land an eligible visitor on the waitlist. */}
          {hasSignupAccess && authLoading ? (
            <button type="button" className={ctaClassName} disabled>
              {t('Loading...')}
            </button>
          ) : hasSignupAccess && !isRealAccount ? (
            <button type="button" onClick={() => setShowSignUpModal(true)} className={ctaClassName}>
              {t('Get started free')}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
            </button>
          ) : (
            <a
              href="https://form.typeform.com/to/VVwyeUrj"
              target="_blank"
              rel="noopener noreferrer"
              className={ctaClassName}
            >
              {t('Request Access')}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
            </a>
          )}
        </div>
      </div>

      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        onSuccess={() => router.push('/select-plan')}
        returnPath="/select-plan"
        defaultMode="signup"
        variant="marketing"
      />
    </div>
  )
}
